import { useEffect, useMemo, useRef, useState } from "react";
import mapboxgl, { GeoJSONSource, Map as MapboxMap, Marker } from "mapbox-gl";
import { supported } from "@mapbox/mapbox-gl-supported";

import { Coordinate } from "common/types";
import { useMapboxCSS } from "common/hooks/mapbox";
import { getMapBounds, isMapboxSourceError, isMapboxTileError } from "common/utils/geo";
import { captureException } from "common/utils/monitoring";

mapboxgl.accessToken = window.MAPBOX_BROWSER_KEY;

export type CommonMapProps = {
  className?: string;
  style?: React.CSSProperties;
  /**
   * A list of coordinate pairs to render markers for
   * Consumers should memoize this prop if performance ever becomes an issue
   * */
  markers?: Coordinate[];
  /**
   * A list of coordinate pairs to draw paths between
   * Consumers should memoize this prop if performance ever becomes an issue
   * */
  paths?: Coordinate[];
};

/**
 * Supported options for customizing the style of the map paths
 * */
type PathOptions = {
  paint: {
    "line-color": string;
    "line-width": number;
  };
};

const ROUTE_SOURCE_ID = "route";
const ROUTE_LAYER_ID = "route";
const noop = () => undefined;

const parseBounds = (serializedBounds: string): mapboxgl.LngLatBoundsLike => {
  return JSON.parse(serializedBounds) as [number, number, number, number];
};

const parseCoordinates = (serializedCoordinates: string): Coordinate[] => {
  return JSON.parse(serializedCoordinates) as Coordinate[];
};

const parsePathOptions = (serializedPathOptions: string): PathOptions => {
  return JSON.parse(serializedPathOptions) as PathOptions;
};

const getRouteData = (coordinates: Coordinate[]) => {
  return {
    type: "Feature" as const,
    properties: {},
    geometry: {
      type: "LineString" as const,
      coordinates,
    },
  };
};

type MapProps = CommonMapProps & {
  /** Which mapbox style to use */
  mapStyle: string;
  /**
   * Callback for creating a map marker, run on each set of coordinates in markers
   * Should be memoized
   * */
  createMarker: (marker: Coordinate) => Marker;
  /**
   * Options for customizing the appearance of the map paths
   * */
  pathOptions: PathOptions;
  /** What to render while mapbox is loading */
  renderLoading: () => React.ReactNode;
  /** What to render when mapbox is unsupported or fails to load */
  renderError: () => React.ReactNode;
  /** Whether the map can be scrolled to zoom */
  disableZoom?: boolean;
};

/** Renders a Map using Mapbox-GL JS
 * When using this componenet, lazy-load it to reduce bundle size (mapbox-gl is 30+mb)
 */
const Map = ({
  markers = [],
  paths = [],
  mapStyle,
  createMarker,
  pathOptions,
  renderLoading,
  renderError,
  disableZoom = false,
  ...other
}: MapProps) => {
  const isMapboxLoaded = useMapboxCSS();
  const [isMapboxSupported, setIsMapboxSupported] = useState(supported());
  const [hasMapLoadFailed, setHasMapLoadFailed] = useState(false);
  const mapContainer = useRef<HTMLDivElement>(null);
  const mapRef = useRef<MapboxMap | null>(null);
  const markerRefs = useRef<Marker[]>([]);
  const serializedMarkers = useMemo(() => JSON.stringify(markers), [markers]);
  const serializedPaths = useMemo(() => JSON.stringify(paths), [paths]);
  const serializedBounds = useMemo(
    () => JSON.stringify(getMapBounds([...markers, ...paths])),
    [markers, paths],
  );
  const serializedPathOptions = useMemo(() => JSON.stringify(pathOptions), [pathOptions]);
  const parsedMarkers = useMemo(() => parseCoordinates(serializedMarkers), [serializedMarkers]);
  const parsedPaths = useMemo(() => parseCoordinates(serializedPaths), [serializedPaths]);
  const parsedBounds = useMemo(() => parseBounds(serializedBounds), [serializedBounds]);
  const parsedPathOptions = useMemo(
    () => parsePathOptions(serializedPathOptions),
    [serializedPathOptions],
  );

  useEffect(() => {
    if (!mapContainer.current || !isMapboxLoaded || !isMapboxSupported || hasMapLoadFailed) {
      return noop;
    }

    try {
      const map = new MapboxMap({
        container: mapContainer.current,
        style: mapStyle,
      });
      let hasStyleLoaded = false;
      map.once("style.load", () => {
        hasStyleLoaded = true;
      });
      // map.loaded() flips back to false while new tiles load, so track the first load instead
      let hasLoaded = false;
      map.once("load", () => {
        hasLoaded = true;
      });
      // mapbox-gl console.errors any error event that has no listener, and Datadog RUM
      // records every console.error. This listener decides each error's outcome instead:
      //   - single tile failures: ignored, the rest of the map still renders
      //   - style or TileJSON failures before the first load: the map cannot render, so
      //     render the error state rather than an empty box
      //   - other failures before the first load (e.g. the sprite request): ignored, the
      //     map still renders, just without icons
      //   - anything after load: likely our own misuse of the map API, so report it
      map.on("error", (event: mapboxgl.ErrorEvent) => {
        if (isMapboxTileError(event)) {
          return;
        }
        if (hasLoaded) {
          captureException(event.error);
          return;
        }
        if (!hasStyleLoaded || isMapboxSourceError(event)) {
          setHasMapLoadFailed(true);
        }
      });
      mapRef.current = map;
    } catch {
      // In case mapbox-gl-supported doesn't work (for very obscure browsers)
      setIsMapboxSupported(false);
    }

    const cleanup = () => {
      markerRefs.current.forEach((marker) => marker.remove());
      markerRefs.current = [];
      mapRef.current?.remove();
      mapRef.current = null;
    };
    return cleanup;
  }, [isMapboxLoaded, isMapboxSupported, hasMapLoadFailed, mapStyle]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return noop;
    }

    if (disableZoom) {
      map.scrollZoom.disable();
    } else {
      map.scrollZoom.enable();
    }
    return noop;
  }, [disableZoom, mapStyle, isMapboxLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return noop;
    }

    let cleanup = noop;
    const fitMapToBounds = () => {
      map.resize();
      map.fitBounds(parsedBounds, { animate: false });
    };

    if (map.isStyleLoaded()) {
      fitMapToBounds();
    } else {
      map.once("load", fitMapToBounds);
      cleanup = () => {
        map.off("load", fitMapToBounds);
      };
    }
    return cleanup;
  }, [parsedBounds, mapStyle, isMapboxLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return noop;
    }

    markerRefs.current.forEach((marker) => marker.remove());
    markerRefs.current = parsedMarkers.map((coordinate) => {
      const marker = createMarker(coordinate);
      marker.addTo(map);
      return marker;
    });
    return noop;
  }, [createMarker, parsedMarkers, mapStyle, isMapboxLoaded]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) {
      return noop;
    }

    let cleanup = noop;
    const updatePath = () => {
      const existingSource = map.getSource(ROUTE_SOURCE_ID);
      const routeData = getRouteData(parsedPaths);

      if (!parsedPaths.length) {
        if (map.getLayer(ROUTE_LAYER_ID)) {
          map.removeLayer(ROUTE_LAYER_ID);
        }
        if (existingSource) {
          map.removeSource(ROUTE_SOURCE_ID);
        }
        return;
      }

      if (existingSource) {
        (existingSource as GeoJSONSource).setData(routeData);
      } else {
        map.addSource(ROUTE_SOURCE_ID, {
          type: "geojson",
          data: routeData,
        });
      }

      if (!map.getLayer(ROUTE_LAYER_ID)) {
        map.addLayer({
          id: ROUTE_LAYER_ID,
          type: "line",
          source: ROUTE_SOURCE_ID,
          layout: {
            "line-join": "round",
            "line-cap": "round",
          },
          ...parsedPathOptions,
        });
        return;
      }

      map.setPaintProperty(ROUTE_LAYER_ID, "line-color", parsedPathOptions.paint["line-color"]);
      map.setPaintProperty(ROUTE_LAYER_ID, "line-width", parsedPathOptions.paint["line-width"]);
    };

    if (map.isStyleLoaded()) {
      updatePath();
    } else {
      map.once("load", updatePath);
      cleanup = () => {
        map.off("load", updatePath);
      };
    }
    return cleanup;
  }, [parsedPaths, parsedPathOptions, mapStyle, isMapboxLoaded]);

  if (!isMapboxSupported || hasMapLoadFailed) {
    return <div {...other}>{renderError()}</div>;
  }

  if (!isMapboxLoaded) {
    return <div {...other}>{renderLoading()}</div>;
  }

  return <div {...other} ref={mapContainer} />;
};

export default Map;
